<script>
    import { createEventDispatcher, onDestroy } from 'svelte';
    import { Rive, Layout, Fit, Alignment, EventType } from '@rive-app/canvas';

    import {
        RIVE_ANIMATIONS,
        changeValueOfAnimationText,
        getAnimationParams,
        getPrefixText
    } from '@src/utils/animations.js';
    import { EventEmitterInstance } from '@root/src/utils/event-emitter';
    import { allowToBet } from '@root/src/store';
    import Backdrop from '../backdrop.svelte';
    import { repeatedlyFillArray } from '@root/src/utils/global';
    import { cashReelsSelectedValue } from '@root/src/store/misc';
    import { playAudio } from '@root/src/utils/audio';

    export let multipliersLeft;
    export let multipliersMiddle;
    export let multipliersRight;
    export let prevMultipliers;

    const stateMachineName = 'State Machine 1';
    const artboardName = 'cash_reels';
    const spinTrigger = 'spin';
    const bonusTrigger = 'top_bonus';
    const chooseLeftTrigger = 'choose_left';
    const chooseMiddleTrigger = 'choose_middle';
    const chooseRightTrigger = 'choose_right';

    const winDoubleBoolean = 'win_double';
    const spinLeftIndex = 'spin_left';
    const spinMiddleIndex = 'spin_middle';
    const spinRightIndex = 'spin_right';

    const customDPR = 1;
    const dispatch = createEventDispatcher();

    let riveInstance;
    let animationParams;
    let sound;
    let selectedSideIndex;

    let timer;
    let timerDoubleSound;
    let timerStopSound;
    let timerChoose;

    const setTextRun = (arr, side) => {
        arr?.forEach((multiplier, index) => {
            const idx = ++index;
            const prefixText = getPrefixText(multiplier);
            changeValueOfAnimationText(
                riveInstance.artboard,
                `text_${idx}_${side}`,
                `${prefixText}${multiplier}`.toLowerCase()
            );
        });
    };

    const setTextWin = multiplier => {
        const prefixText = getPrefixText(multiplier);

        let winText = multiplier === 'DOUBLE' ? 'win_double' : 'win';

        changeValueOfAnimationText(
            riveInstance.artboard,
            `${winText}_fill`,
            `${prefixText}${multiplier}`.toLowerCase()
        );
        changeValueOfAnimationText(
            riveInstance.artboard,
            `${winText}_stroke`,
            `${prefixText}${multiplier}`.toLowerCase()
        );
        changeValueOfAnimationText(
            riveInstance.artboard,
            `${winText}_shadow`,
            `${prefixText}${multiplier}`.toLowerCase()
        );
    };

    const runAnimation = canvas => {
        if (riveInstance) {
            riveInstance.cleanup();
        }

        riveInstance = new Rive({
            src: RIVE_ANIMATIONS.CASH_REELS,
            stateMachines: stateMachineName,
            canvas: canvas,
            autoplay: true,
            artboard: artboardName,
            shouldDisableRiveListeners: false,
            layout: new Layout({
                fit: Fit.Cover,
                alignment: Alignment.Center
            }),
            onLoad: () => {
                riveInstance.resizeDrawingSurfaceToCanvas(customDPR);
                animationParams = getAnimationParams(riveInstance, stateMachineName, [
                    spinTrigger,
                    bonusTrigger,
                    chooseLeftTrigger,
                    chooseMiddleTrigger,
                    chooseRightTrigger,
                    spinLeftIndex,
                    spinMiddleIndex,
                    spinRightIndex,
                    winDoubleBoolean
                ]);

                $allowToBet = false;
                animationParams[winDoubleBoolean].value = false;

                setTextRun(repeatedlyFillArray(prevMultipliers[0] || multipliersLeft, 6), 'left');
                setTextRun(repeatedlyFillArray(prevMultipliers[1] || multipliersMiddle, 6), 'middle');
                setTextRun(repeatedlyFillArray(prevMultipliers[2] || multipliersRight, 6), 'right');

                if (riveInstance) {
                    EventEmitterInstance.on('top-slot-bonusTrigger', () => {
                        timer = setTimeout(() => {
                            animationParams[bonusTrigger].fire();

                            setTextRun(repeatedlyFillArray(multipliersLeft, 6), 'left');
                            setTextRun(repeatedlyFillArray(multipliersMiddle, 6), 'middle');
                            setTextRun(repeatedlyFillArray(multipliersRight, 6), 'right');
                        }, 700);
                    });
                }

                EventEmitterInstance.on('response-bonus-cash-reels', response => {
                    const isFinal = response?.resp?.final_result;
                    if (!isFinal) {
                        const multLeft = response?.resp?.cash_reels_params?.reels[0];
                        const multMiddle = response?.resp?.cash_reels_params?.reels[1];
                        const multRight = response?.resp?.cash_reels_params?.reels[2];

                        setTextRun(repeatedlyFillArray(multLeft, 6), 'left');
                        setTextRun(repeatedlyFillArray(multMiddle, 6), 'middle');
                        setTextRun(repeatedlyFillArray(multRight, 6), 'right');
                    }

                    const data = response?.resp?.cash_reels_result;

                    animationParams[spinLeftIndex].value = multipliersLeft.indexOf(data?.sectors[0]) + 1;
                    animationParams[spinMiddleIndex].value = multipliersLeft.indexOf(data?.sectors[1]) + 1;
                    animationParams[spinRightIndex].value = multipliersLeft.indexOf(data?.sectors[2]) + 1;
                    animationParams[spinTrigger].fire();

                    if (data?.sectors[selectedSideIndex] === 'DOUBLE') {
                        animationParams[winDoubleBoolean].value = true;

                        timerDoubleSound = setTimeout(() => {
                            playAudio('audio_wof_double_win');
                        }, 11000);
                    }

                    timerStopSound = setTimeout(() => {
                        playAudio('audio_cash_reels_stop');
                    }, 10000);

                    timerChoose = setTimeout(() => {
                        playAudio('audio_cash_reels_mult_win');
                    }, 12000);

                    setTextWin(data?.sectors[selectedSideIndex]);
                });
            },
            onStateChange: event => {
                const eventName = event?.data?.[0];
                // console.log('STATE:', eventName);

                if (eventName === 'choose_left') {
                    selectedSideIndex = 0;
                    $cashReelsSelectedValue = 1;
                    animationParams[chooseLeftTrigger].fire();
                }
                if (eventName === 'choose_middle') {
                    selectedSideIndex = 1;
                    $cashReelsSelectedValue = 2;
                    animationParams[chooseMiddleTrigger].fire();
                }
                if (eventName === 'choose_right') {
                    selectedSideIndex = 2;
                    $cashReelsSelectedValue = 3;
                    animationParams[chooseRightTrigger].fire();
                }

                if (eventName.match(/^choose_(left|middle|right)$/)) {
                    $allowToBet = true;
                    playAudio('audio_cash_reels_choice');
                }

                if (eventName === 'exit') {
                    EventEmitterInstance.emit('promise-end');
                }
            }
        });
    };

    onDestroy(() => {
        EventEmitterInstance.removeListener('top-slot-bonusTrigger');
        EventEmitterInstance.removeListener('response-bonus-cash-reels');
        EventEmitterInstance.removeListener('promise-end');
        riveInstance?.cleanup();
        clearTimeout(timer);
        clearTimeout(timerDoubleSound);
        clearTimeout(timerStopSound);
        clearTimeout(timerChoose);
        sound?.unload();
    });
</script>

<div class="fixed inset-0">
    <Backdrop />
</div>
<div class="absolute inset-0 z-9000 mx-auto h-full w-full">
    <canvas class="h-full w-full" width="500" height="570" use:runAnimation />
</div>
